/**
 * Iter XX.142 — Founder activation funnel dashboard.
 *
 * Real-time (24 h rolling window by default) view of the primary
 * business metric — landing → first coaching moment. Every stage
 * shows raw count, conversion from previous stage, conversion from
 * landing_view, AND the median landing → first-coaching latency.
 *
 * Server: GET /api/admin/founder/activation-funnel (admin-gated).
 */
import React, { useEffect, useState } from "react";
import api from "../lib/api";

function pctColour(v) {
  if (typeof v !== "number") return "text-neutral-500";
  if (v >= 40) return "text-emerald-300";
  if (v >= 15) return "text-amber-300";
  return "text-rose-300";
}

function formatSeconds(s) {
  if (typeof s !== "number" || s <= 0) return "—";
  if (s < 60) return `${Math.round(s)} s`;
  const m = Math.floor(s / 60);
  const rest = Math.round(s - m * 60);
  return `${m} m ${rest} s`;
}

function StageRow({ stage, idx, maxCount }) {
  const barPct = maxCount > 0 ? Math.round((stage.count / maxCount) * 100) : 0;
  return (
    <tr
      data-testid={`funnel-stage-${stage.event}`}
      className="border-b border-neutral-800/60"
    >
      <td className="py-3 px-3 text-xs text-neutral-500 tabular-nums w-8">
        {idx + 1}
      </td>
      <td className="py-3 px-3 font-mono text-sm text-neutral-100">
        {stage.event}
      </td>
      <td className="py-3 px-3">
        <div className="flex items-center gap-3">
          <div className="w-40 h-1.5 bg-neutral-800 rounded overflow-hidden">
            <div className="h-full bg-emerald-500" style={{ width: `${barPct}%` }} />
          </div>
          <span
            data-testid={`funnel-count-${stage.event}`}
            className="text-sm text-neutral-100 tabular-nums w-16 text-right"
          >
            {stage.count.toLocaleString()}
          </span>
        </div>
      </td>
      <td
        data-testid={`funnel-pct-prev-${stage.event}`}
        className={`py-3 px-3 text-xs tabular-nums text-right ${pctColour(stage.pct_of_previous)}`}
      >
        {stage.pct_of_previous.toFixed(1)}%
      </td>
      <td
        data-testid={`funnel-pct-landing-${stage.event}`}
        className={`py-3 px-3 text-xs tabular-nums text-right ${pctColour(stage.pct_of_landing)}`}
      >
        {stage.pct_of_landing.toFixed(1)}%
      </td>
    </tr>
  );
}

export default function AdminActivationFunnel() {
  const [windowHours, setWindowHours] = useState(24);
  const [landingFilter, setLandingFilter] = useState("");
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    document.title = "Activation Funnel — Founder";
  }, []);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      params.set("window_hours", String(windowHours));
      if (landingFilter.trim()) params.set("landing_page_id", landingFilter.trim());
      const res = await api.get(
        `/admin/founder/activation-funnel?${params.toString()}`,
      );
      setData(res.data);
    } catch (e) {
      const status = e?.response?.status;
      const msg = e?.response?.data?.detail || e.message || String(e);
      setError(`${status || "err"}: ${msg}`);
    } finally {
      setLoading(false);
    }
  };

  // Initial + 30 s auto-refresh so this reads like a real dashboard.
  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 30000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowHours, landingFilter]);

  const maxCount = data?.stages?.reduce(
    (m, s) => Math.max(m, s.count || 0), 0,
  ) || 0;

  return (
    <div
      data-testid="admin-activation-funnel-page"
      className="min-h-screen bg-neutral-950 text-neutral-100 p-6"
    >
      <div className="max-w-4xl mx-auto">
        <div className="mb-6 flex items-baseline justify-between">
          <div>
            <h1
              data-testid="activation-funnel-title"
              className="text-2xl font-semibold"
            >
              Activation Funnel
            </h1>
            <p className="text-sm text-neutral-400 mt-1">
              Landing → First Coaching Moment. Every second before the first
              coaching moment is wasted acquisition cost.
            </p>
          </div>
          <div className="text-xs text-neutral-500">
            Auto-refresh 30 s
          </div>
        </div>

        <div
          data-testid="activation-funnel-filters"
          className="flex flex-wrap gap-2 mb-6 items-center"
        >
          <label className="text-xs text-neutral-500">Window</label>
          <select
            data-testid="activation-funnel-window"
            value={windowHours}
            onChange={(e) => setWindowHours(Number(e.target.value))}
            className="bg-neutral-900 border border-neutral-800 rounded px-3 py-2 text-sm"
          >
            <option value={1}>1 h</option>
            <option value={6}>6 h</option>
            <option value={24}>24 h</option>
            <option value={72}>3 d</option>
            <option value={168}>7 d</option>
          </select>
          <label className="text-xs text-neutral-500">Landing</label>
          <input
            data-testid="activation-funnel-landing"
            type="text"
            value={landingFilter}
            onChange={(e) => setLandingFilter(e.target.value)}
            placeholder="home / confidence / bowling / parents / …"
            className="flex-1 min-w-[240px] bg-neutral-900 border border-neutral-800 rounded px-3 py-2 text-sm font-mono"
          />
          <button
            type="button"
            data-testid="activation-funnel-refresh"
            onClick={refresh}
            disabled={loading}
            className="bg-emerald-700 hover:bg-emerald-600 disabled:opacity-50 rounded px-4 py-2 text-sm font-medium"
          >
            {loading ? "Loading…" : "Refresh"}
          </button>
        </div>

        {error ? (
          <div
            data-testid="activation-funnel-error"
            className="mb-4 border border-rose-900 bg-rose-950/30 text-rose-200 rounded p-3 text-sm"
          >
            {error}
          </div>
        ) : null}

        {data ? (
          <>
            <div
              data-testid="activation-funnel-headline"
              className="mb-6 border border-emerald-900/60 bg-emerald-950/20 rounded p-4"
            >
              <div className="text-[11px] uppercase tracking-wider text-emerald-400 mb-1">
                Median landing → first coaching moment
              </div>
              <div className="text-2xl font-semibold text-emerald-100 tabular-nums">
                {formatSeconds(data.median_landing_to_first_coaching_seconds)}
              </div>
              <div className="text-[11px] text-emerald-300/80 mt-1">
                across {data.n_sessions_measured_for_latency || 0} completed
                sessions in the last {data.window_hours} h
              </div>
            </div>

            <table className="w-full text-left border-collapse mb-8">
              <thead>
                <tr className="text-[11px] uppercase tracking-wider text-neutral-500 border-b border-neutral-800">
                  <th className="py-2 px-3 w-8">#</th>
                  <th className="py-2 px-3">Stage</th>
                  <th className="py-2 px-3">Count</th>
                  <th className="py-2 px-3 text-right w-24">% prev</th>
                  <th className="py-2 px-3 text-right w-24">% landing</th>
                </tr>
              </thead>
              <tbody>
                {(data.stages || []).map((s, i) => (
                  <StageRow key={s.event} stage={s} idx={i} maxCount={maxCount} />
                ))}
              </tbody>
            </table>

            {(data.landing_page_breakdown || []).length ? (
              <div className="mb-8">
                <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-2">
                  By landing page
                </div>
                <table className="w-full text-left border-collapse">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-wider text-neutral-500 border-b border-neutral-800">
                      <th className="py-2 px-3">Landing</th>
                      <th className="py-2 px-3 text-right">Views</th>
                      <th className="py-2 px-3 text-right">Coaching moments</th>
                      <th className="py-2 px-3 text-right">Conversion</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.landing_page_breakdown.map((b) => (
                      <tr
                        key={b.landing_page_id}
                        data-testid={`funnel-bucket-${b.landing_page_id}`}
                        className="border-b border-neutral-800/60"
                      >
                        <td className="py-2 px-3 font-mono text-sm">
                          {b.landing_page_id}
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums">
                          {(b.counts?.landing_view || 0).toLocaleString()}
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums">
                          {(b.counts?.first_coaching_moment || 0).toLocaleString()}
                        </td>
                        <td className={`py-2 px-3 text-right tabular-nums ${pctColour(b.landing_to_first_coaching_pct)}`}>
                          {b.landing_to_first_coaching_pct.toFixed(1)}%
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}

            {/* Iter XX.172 — Focused Entry Chips experiment breakdown.
                CONTROL vs FOCUSED — chip taps, entry completion,
                second-message rate, Outcome %, Plus %, paid %, and
                the primary founder metric: Outcome per chip start. */}
            {(data.experiment_breakdown || []).length ? (
              <div className="mb-8" data-testid="xx172-experiment-breakdown">
                <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-2">
                  XX.172 — Focused Entry Chips (arm)
                </div>
                <table className="w-full text-left border-collapse text-sm">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-wider text-neutral-500 border-b border-neutral-800">
                      <th className="py-2 px-3">Arm</th>
                      <th className="py-2 px-3 text-right">Chip taps</th>
                      <th className="py-2 px-3 text-right">1st msg sent</th>
                      <th className="py-2 px-3 text-right">Entry %</th>
                      <th className="py-2 px-3 text-right">2nd msg %</th>
                      <th className="py-2 px-3 text-right">4+ %</th>
                      <th className="py-2 px-3 text-right">Outcome</th>
                      <th className="py-2 px-3 text-right">Outcome/chip</th>
                      <th className="py-2 px-3 text-right">Plus seen</th>
                      <th className="py-2 px-3 text-right">Paid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.experiment_breakdown.map((b) => (
                      <tr
                        key={b.key}
                        data-testid={`xx172-arm-${b.key}`}
                        className="border-b border-neutral-800/60"
                      >
                        <td className="py-2 px-3 font-mono">{b.key}</td>
                        <td className="py-2 px-3 text-right tabular-nums">
                          {(b.counts?.entry_chip_tapped || 0).toLocaleString()}
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums">
                          {(b.counts?.primary_cta_clicked || 0).toLocaleString()}
                        </td>
                        <td className={`py-2 px-3 text-right tabular-nums ${pctColour(b.entry_completion_pct)}`}>
                          {b.entry_completion_pct.toFixed(1)}%
                        </td>
                        <td className={`py-2 px-3 text-right tabular-nums ${pctColour(b.second_user_message_pct)}`}>
                          {b.second_user_message_pct.toFixed(1)}%
                        </td>
                        <td className={`py-2 px-3 text-right tabular-nums ${pctColour(b.four_plus_pct)}`}>
                          {b.four_plus_pct.toFixed(1)}%
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums">
                          {(b.counts?.completed_arc || 0).toLocaleString()}
                        </td>
                        <td className={`py-2 px-3 text-right tabular-nums font-semibold ${pctColour(b.outcome_per_chip_tap_pct)}`}>
                          {b.outcome_per_chip_tap_pct.toFixed(1)}%
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums">
                          {(b.counts?.plus_seen || 0).toLocaleString()}
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums">
                          {(b.counts?.subscription_started || 0).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div className="mt-2 text-[11px] text-neutral-500">
                  Outcome/chip is the north-star metric: activated players per chip start,
                  independent of any conditional rate drop.
                </div>
              </div>
            ) : null}

            {/* Iter XX.172 — Focused arm situation breakdown. Only
                shown when at least one focused chip has been picked. */}
            {(data.situation_breakdown || []).length ? (
              <div className="mb-8" data-testid="xx172-situation-breakdown">
                <div className="text-[11px] uppercase tracking-wider text-neutral-500 mb-2">
                  XX.172 — Focused situations (focused arm only)
                </div>
                <table className="w-full text-left border-collapse text-sm">
                  <thead>
                    <tr className="text-[11px] uppercase tracking-wider text-neutral-500 border-b border-neutral-800">
                      <th className="py-2 px-3">Problem : situation</th>
                      <th className="py-2 px-3 text-right">Selected</th>
                      <th className="py-2 px-3 text-right">2nd msg %</th>
                      <th className="py-2 px-3 text-right">Outcome</th>
                      <th className="py-2 px-3 text-right">Paid</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.situation_breakdown.map((b) => (
                      <tr
                        key={b.key}
                        data-testid={`xx172-situation-${b.key}`}
                        className="border-b border-neutral-800/60"
                      >
                        <td className="py-2 px-3 font-mono">{b.key}</td>
                        <td className="py-2 px-3 text-right tabular-nums">
                          {(b.counts?.primary_cta_clicked || 0).toLocaleString()}
                        </td>
                        <td className={`py-2 px-3 text-right tabular-nums ${pctColour(b.second_user_message_pct)}`}>
                          {b.second_user_message_pct.toFixed(1)}%
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums">
                          {(b.counts?.completed_arc || 0).toLocaleString()}
                        </td>
                        <td className="py-2 px-3 text-right tabular-nums">
                          {(b.counts?.subscription_started || 0).toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}
